/**
 * BI Command Center category registry — WQ-253.
 *
 * Maps the 18 functional sub-tabs into 6 navigation categories. Sub-tab
 * keys preserve their pre-WQ-253 values for URL backward compatibility
 * (existing `?tab=` deep-links resolve via registry lookup at mount time).
 *
 * Adding a new sub-tab: append to the appropriate category's `subTabs`
 * array. Adding a new category: append to BI_CC_CATEGORIES; pick a
 * distinct lucide-react icon; ensure the category key is URL-safe.
 *
 * Slotting (18 sub-tabs / 6 categories) — established 2026-04-30 from
 * the actual BICommandCenterPage tab list:
 *   Data       (4) — data-sources / datasets / data-prep / data-quality
 *   Modeling   (3) — algorithms / automl / experiments
 *   Inference  (2) — predictions / monte-carlo
 *   Lifecycle  (3) — lifecycles / commissioning / ops-agent
 *   Trust      (5) — fairness / explainability / model-health / security / cyber-agent
 *   Billing    (1) — usage-aggregation
 *
 * Note: registry stores only the *primary panel component* per sub-tab.
 * The 4 launcher-tabs (monte-carlo, fairness, explainability, data-quality)
 * have additional UI (action button + modal) that BICommandCenterPage
 * maintains state for and renders conditionally on `activeSubTab`.
 */

import type { ComponentType } from 'react';
import {
  Database,
  Brain,
  Send,
  Activity,
  ShieldCheck,
  Receipt,
} from 'lucide-react';

import AlgorithmBrowser from './AlgorithmBrowser';
import DataSourceCatalog from './DataSourceCatalog';
import DatasetComposer from './DatasetComposer';
import DataPrepPanel from './DataPrepPanel';
import AutoMLDashboard from './AutoMLDashboard';
import PredictionPanel from './PredictionPanel';
import ExperimentExplorer from './ExperimentExplorer';
import LifecycleTimeline from './LifecycleTimeline';
import CommissioningReview from './CommissioningReview';
import FairnessPanel from './FairnessPanel';
import ExplainabilityPanel from './ExplainabilityPanel';
import DataQualityPanel from './DataQualityPanel';
import ModelHealthPanel from './ModelHealthPanel';
import SecurityTrustPanel from './SecurityTrustPanel';
import OpsAgentPanel from './OpsAgentPanel';
import CyberAgentPanel from './CyberAgentPanel';
import MonteCarloPanel from './MonteCarloPanel';
import UsageAggregationDashboard from './UsageAggregationDashboard';

export interface BiSubTab {
  /** URL-safe key; preserved from pre-WQ-253 for back-compat */
  key: string;
  /** Display name */
  label: string;
  /** Primary panel component rendered inside BiPanelErrorBoundary.
   * `any` (not `unknown`) because the existing panel components are
   * `React.FC` (i.e., `FC<{}>`); `ComponentType<unknown>` rejects them
   * via the contravariant props check. */
  component: ComponentType<any>;
}

export interface BiCategory {
  /** URL-safe key; e.g., 'data', 'modeling' */
  key: string;
  /** Display name */
  label: string;
  /** lucide-react icon component */
  icon: typeof Database;
  subTabs: BiSubTab[];
}

export const BI_CC_CATEGORIES: BiCategory[] = [
  {
    key: 'data',
    label: 'Data',
    icon: Database,
    subTabs: [
      { key: 'data-sources', label: 'Data Sources', component: DataSourceCatalog },
      { key: 'datasets', label: 'Datasets', component: DatasetComposer },
      { key: 'data-prep', label: 'Data Prep', component: DataPrepPanel },
      { key: 'data-quality', label: 'Data Quality', component: DataQualityPanel },
    ],
  },
  {
    key: 'modeling',
    label: 'Modeling',
    icon: Brain,
    subTabs: [
      { key: 'algorithms', label: 'Algorithms', component: AlgorithmBrowser },
      { key: 'automl', label: 'AutoML', component: AutoMLDashboard },
      { key: 'experiments', label: 'Experiments', component: ExperimentExplorer },
    ],
  },
  {
    key: 'inference',
    label: 'Inference',
    icon: Send,
    subTabs: [
      { key: 'predictions', label: 'Predictions', component: PredictionPanel },
      { key: 'monte-carlo', label: 'Monte Carlo', component: MonteCarloPanel },
    ],
  },
  {
    key: 'lifecycle',
    label: 'Lifecycle',
    icon: Activity,
    subTabs: [
      { key: 'lifecycles', label: 'Lifecycles', component: LifecycleTimeline },
      { key: 'commissioning', label: 'Commissioning', component: CommissioningReview },
      { key: 'ops-agent', label: 'OpsAgent', component: OpsAgentPanel },
    ],
  },
  {
    key: 'trust',
    label: 'Trust',
    icon: ShieldCheck,
    subTabs: [
      { key: 'fairness', label: 'Fairness', component: FairnessPanel },
      { key: 'explainability', label: 'Explainability', component: ExplainabilityPanel },
      { key: 'model-health', label: 'Model Health', component: ModelHealthPanel },
      { key: 'security', label: 'Security', component: SecurityTrustPanel },
      { key: 'cyber-agent', label: 'CyberAgent', component: CyberAgentPanel },
    ],
  },
  {
    key: 'billing',
    label: 'Billing',
    icon: Receipt,
    subTabs: [
      {
        key: 'usage-aggregation',
        label: 'Usage & Settlements',
        component: UsageAggregationDashboard,
      },
    ],
  },
];

/** Reverse lookup: given a sub-tab key, return its category. */
export function findCategoryForSubTab(subTabKey: string): BiCategory | undefined {
  return BI_CC_CATEGORIES.find((c) => c.subTabs.some((s) => s.key === subTabKey));
}

/** Get the default (first) sub-tab key for a category. */
export function defaultSubTabForCategory(categoryKey: string): string | undefined {
  const cat = BI_CC_CATEGORIES.find((c) => c.key === categoryKey);
  return cat?.subTabs[0]?.key;
}

/** Default category at mount (when no URL param). */
export const DEFAULT_BI_CATEGORY = 'modeling';
/** Default sub-tab at mount (when no URL param). Matches pre-WQ-253 default. */
export const DEFAULT_BI_SUB_TAB = 'algorithms';
